import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../../components/public/Header";
import Footer from "../../components/public/Footer";
import TrustedBy from "../../components/public/TrustedBy";
import { ArrowLeft, Briefcase, MapPin, Clock, CheckCircle, PaperclipHorizontal } from "@phosphor-icons/react";
import usePageMeta from "../../hooks/usePageMeta";
import Captcha from "../../components/public/Captcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", resume_url: "" });
  const [captcha, setCaptcha] = useState({ token: "", answer: "" });
  const [captchaKey, setCaptchaKey] = useState(0);

  usePageMeta({
    title: item ? (item.seo?.meta_title || `${item.title} — Careers at Bon-Heur Pharma`) : "Careers — Bon-Heur Pharma",
    description: item ? (item.seo?.meta_description || (item.description ? item.description.slice(0, 160) : undefined)) : undefined,
    canonical: item?.seo?.canonical_url,
    ogType: "article",
    keywords: item ? `${item.title}, ${item.location || ""}, ${item.department || ""}, pharma careers, Bon-Heur jobs`.replace(/,\s*,/g, ",") : "pharma careers, Bon-Heur Pharmaceuticals",
    robots: item?.seo?.robots,
  });

  useEffect(() => {
    axios.get(`${API}/public/careers/${slug}`).then((r) => setItem(r.data)).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true);
    });
  }, [slug]);

  useEffect(() => {
    if (!item?.jsonld) return;
    const scripts = item.jsonld.map((blk) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(blk);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => document.head.removeChild(s));
  }, [item]);

  const uploadResume = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/public/upload-resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, resume_url: r.data.url }));
      toast.success("Resume attached");
    } catch (err) {
      toast.error("Could not upload resume");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!captcha.token || !captcha.answer) { toast.error("Please solve the CAPTCHA"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/applications`, {
        job_id: item.id,
        ...form,
        captcha_token: captcha.token,
        captcha_answer: captcha.answer,
      });
      setSubmitted(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
      setCaptchaKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-[#1F2937]">Role not found</h1>
          <button onClick={() => navigate("/careers")} className="mt-6 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-semibold">Back to careers</button>
        </div>
        <Footer />
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20"><div className="h-96 bg-[#F1F5F9] rounded-2xl animate-pulse" /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="career-detail">
      <Header />
      <main>
        <div className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-[#64748B]">
            <Link to="/careers" className="inline-flex items-center gap-1.5 hover:text-[#C2410C]"><ArrowLeft size={12} /> All openings</Link>
          </div>
        </div>

        <section className="py-12 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif", animation: "blurUp 0.7s ease-out both" }}>{item.title}</h1>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#64748B]" style={{ animation: "blurUp 0.8s ease-out 0.15s both" }}>
              {item.department && <span className="inline-flex items-center gap-1.5"><Briefcase size={14} className="text-[#C2410C]" /> {item.department}</span>}
              {item.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#C2410C]" /> {item.location}</span>}
              {item.employment_type && <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#C2410C]" /> {item.employment_type}</span>}
              {item.experience && <span className="inline-flex items-center gap-1.5">Experience: <b className="text-[#1F2937]">{item.experience}</b></span>}
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>About the role</h2>
                <div className="mt-4 text-[#374151] leading-relaxed whitespace-pre-line">{item.description}</div>
                {item.requirements && (
                  <>
                    <h2 className="mt-10 text-2xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>What you&apos;ll bring</h2>
                    <div className="mt-4 text-[#374151] leading-relaxed whitespace-pre-line">{item.requirements}</div>
                  </>
                )}
              </div>

              <aside className="lg:col-span-1 lg:sticky lg:top-24 self-start">
                <div className="rounded-2xl border border-[#E2E8F0] p-6 bg-[#F8FAFC]" data-testid="apply-card">
                  {submitted ? (
                    <div className="text-center py-6">
                      <CheckCircle size={44} weight="fill" className="mx-auto text-[#0EA5E9] mb-3" />
                      <h3 className="text-lg font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Application received</h3>
                      <p className="mt-2 text-sm text-[#64748B]">Thank you for applying. Our team will review and reach out soon.</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>Apply for this role</h3>
                      <p className="mt-1 text-xs text-[#64748B]">All fields marked * are required.</p>
                      <form onSubmit={submit} className="mt-5 space-y-3">
                        <Input label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="apply-name" />
                        <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="apply-email" />
                        <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="apply-phone" />
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">Cover letter</label>
                          <textarea
                            value={form.cover_letter}
                            onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                            rows={4}
                            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition resize-none"
                            data-testid="apply-cover"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">Resume (PDF/DOC)</label>
                          <label className="mt-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-[#CBD5E1] hover:border-[#C2410C] text-sm text-[#64748B] cursor-pointer transition">
                            <PaperclipHorizontal size={14} />
                            <span className="truncate flex-1">{form.resume_url ? form.resume_url.split("/").pop() : "Attach resume"}</span>
                            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadResume(e.target.files?.[0])} data-testid="apply-resume" />
                          </label>
                        </div>
                        <Captcha
                          onChange={setCaptcha}
                          refreshKey={captchaKey}
                          testId="apply-captcha"
                        />
                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full px-4 py-3 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold transition disabled:opacity-60"
                          data-testid="apply-submit"
                        >
                          {submitting ? "Submitting..." : "Submit application"}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <TrustedBy />
      <Footer />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition"
        data-testid={testid}
      />
    </div>
  );
}
